import React from 'react';
import { Send, Copy, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const inputClass = 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500';
const SMS_LENGTH = 160;

const CharCount = ({ length }) => (
  <span className={`text-xs ${length > SMS_LENGTH ? 'text-yellow-400' : 'text-gray-500'}`} data-testid="sms-char-count">
    {length}/{SMS_LENGTH} characters
    {length > SMS_LENGTH && ` (${Math.ceil(length / SMS_LENGTH)} SMS)`}
  </span>
);

const MessagePreview = ({ message, onCopy }) => (
  <div className="bg-gray-800 rounded-lg p-4 border border-gray-700" data-testid="sms-preview">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-gray-500 uppercase">Preview</span>
      <button onClick={onCopy} className="text-gray-400 hover:text-white" data-testid="sms-copy-button" aria-label="Copy message"><Copy className="w-4 h-4" /></button>
    </div>
    <div className="bg-green-600 text-white p-3 rounded-lg rounded-tl-none max-w-sm">
      <p className="text-sm whitespace-pre-wrap">{message}</p>
    </div>
  </div>
);

export const ComposeMessage = ({ name, phone, message, onChange, onSend, onCopy, sending, twilioConfigured }) => (
  <Card className="bg-gray-900 border-gray-800">
    <CardHeader>
      <CardTitle className="text-white flex items-center"><Send className="w-5 h-5 mr-2 text-yellow-400" />Compose Message</CardTitle>
      <CardDescription className="text-gray-500">Customize and send your message</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-gray-400 mb-2 block">Customer Name</label>
          <Input type="text" placeholder="John Smith" value={name} onChange={(e) => onChange('name', e.target.value)} className={inputClass} data-testid="sms-customer-name-input" />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-400 mb-2 block">Phone Number</label>
          <Input type="tel" placeholder="(929) 555-1234" value={phone} onChange={(e) => onChange('phone', e.target.value)} className={inputClass} data-testid="sms-phone-input" />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-gray-400">Message</label>
          <CharCount length={message.length} />
        </div>
        <Textarea placeholder="Type your message or select a template..." value={message} onChange={(e) => onChange('message', e.target.value)} rows={6} className={inputClass} data-testid="sms-message-input" />
      </div>
      {message && <MessagePreview message={message} onCopy={onCopy} />}
      <div className="flex items-center justify-between pt-4 border-t border-gray-800">
        <div className="text-sm text-gray-500">
          {!twilioConfigured && <span className="text-yellow-400">⚠ Demo mode - SMS will be logged but not sent</span>}
        </div>
        <Button onClick={onSend} disabled={sending || !phone || !message} className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-8" data-testid="sms-send-button">
          {sending ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          {sending ? 'Sending...' : 'Send SMS'}
        </Button>
      </div>
    </CardContent>
  </Card>
);
